import { Box } from '@material-ui/core'
import { GenericContentPopper } from 'components/controls/misc/GenericContentPopper'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import ImagePopover from 'components/facets/ImagePopover'
import { useOrderStore } from 'lib/hooks'
import { useEffect, useState } from 'react'
import { AutoSizer, Column, Table } from 'react-virtualized'
import { OrderArticle } from 'shared'
import { getProductImageUrl } from 'utils/imageUtils'
import { ColorBox } from './ColorBox'

// The columns that shrink stop at SHRINKING_COLUMN_MIN_WIDTH, otherwise on narrow screens they reach zero
// and disappear: below the sum of all minimum widths the table scrolls sideways instead. The sum counts
// the 10px margin react-virtualized's stylesheet puts after each of the 8 columns, or the last one is cut.
const SHRINKING_COLUMN_MIN_WIDTH = 80
const TABLE_MIN_WIDTH = 690 + 8 * 10
const DIALOG_TABLE_MIN_HEIGHT = 300
// import { useInventoryStore } from 'lib/hooks/useInventoryStore'

export interface ArticlesTableProps {
  articles: OrderArticle[]
  setOpenReportIssueDialog: (open: boolean) => void
  parentPage?: 'ordersTable' | 'dialog'
  viewOnly?: boolean
}

export const ArticlesTable = ({
  articles,
  setOpenReportIssueDialog,
  parentPage = 'ordersTable',
  viewOnly,
}: ArticlesTableProps): JSX.Element => {
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)
  const selectedArticle = useOrderStore((state) => state.selectedArticle)
  const setSelectedArticle = useOrderStore((state) => state.setSelectedArticle)

  const [anchorIssueBtn, setAnchorIssueBtn] = useState<HTMLElement | null>(null)
  const [openIssueDetailPopper, setOpenIssueDetailPopper] = useState(false)
  setOpenIssueDetailPopper

  //update windows size when change, so we have responsive design
  useEffect(() => {
    function updateWindowDimensions() {
      setWindowHeight(window.innerHeight)
    }

    window.addEventListener('resize', updateWindowDimensions)
    return () => window.removeEventListener('resize', updateWindowDimensions)
  }, [])

  return (
    <>
      <div className="overflow-auto">
        <AutoSizer disableHeight>
          {({ width }) => (
            <Table
              className="articles-table"
              width={Math.max(width, TABLE_MIN_WIDTH)}
              height={
                parentPage === 'dialog'
                  ? Math.max(windowHeight - 500, DIALOG_TABLE_MIN_HEIGHT)
                  : windowHeight - 500
              }
              headerHeight={35}
              rowHeight={45}
              rowCount={articles.length}
              rowGetter={({ index }) => articles[index]}
            >
              <Column
                width={60}
                flexGrow={0}
                flexShrink={0}
                label="Img"
                dataKey=""
                disableSort={true}
                cellRenderer={({ rowData: a }: { rowData: OrderArticle }) => (
                  <ImagePopover
                    variant="iconPopover"
                    imageComponent={
                      <img width="62px" src={getProductImageUrl(a.idProduct?.toString())} />
                    }
                    imageId={a.idProduct?.toString()}
                  />
                )}
              />

              <Column
                width={140}
                flexGrow={1}
                flexShrink={1}
                minWidth={SHRINKING_COLUMN_MIN_WIDTH}
                label="Name"
                dataKey="name"
                disableSort={true}
                cellRenderer={({ rowData: a }: { rowData: OrderArticle }) => (
                  <div>
                    <div>{a.getLocalizedName()}</div>
                    {a.idLanguage !== 1 && <div>[{a.card.name}]</div>}
                  </div>
                )}
              />

              <Column
                width={140}
                flexGrow={1}
                flexShrink={1}
                minWidth={SHRINKING_COLUMN_MIN_WIDTH}
                disableSort={true}
                label="Cn - Expansion"
                dataKey="expansion"
                cellRenderer={({ rowData: a }: { rowData: OrderArticle }) => (
                  <div className="d-flex flex-column">
                    <Box className="articles-table-ellipsis">
                      #{a.card.cn} - {a.card.set}
                    </Box>
                    {/* native title: this table can render a lot of rows, and a
                        MUI Tooltip would mount a Popper per cell */}
                    <Box className="articles-table-ellipsis" title={a.card.expansion}>
                      {a.card.expansion}
                    </Box>
                  </div>
                )}
              />

              <Column
                width={100}
                flexGrow={0}
                flexShrink={0}
                disableSort={true}
                label="Color - Rarity"
                dataKey="rarity"
                cellRenderer={({ rowData: a }: { rowData: OrderArticle }) => (
                  <div className="d-flex flex-column">
                    <span>{a.card?.colors}</span>
                    <span>{a.card?.rarityStr}</span>
                  </div>
                )}
              />

              <Column
                width={100}
                flexGrow={0}
                flexShrink={0}
                disableSort={true}
                label="Quantity"
                dataKey="quantity"
                headerClassName="centered-header"
                cellRenderer={({ rowData: a }: { rowData: OrderArticle }) => (
                  <div className="text-center">{a.quantity} </div>
                )}
              />

              <Column
                width={140}
                flexGrow={1}
                flexShrink={1}
                minWidth={SHRINKING_COLUMN_MIN_WIDTH}
                label="Properties"
                dataKey=""
                disableSort={true}
                cellRenderer={({ rowData: a }) => {
                  return <ArticleAttributes article={a} iconHeight={17} />
                }}
              />

              <Column
                width={140}
                flexGrow={1}
                flexShrink={1}
                minWidth={SHRINKING_COLUMN_MIN_WIDTH}
                disableSort={true}
                label="Comments"
                dataKey="comments"
                cellRenderer={({ rowData: a }: { rowData: OrderArticle }) => (
                  <div className="articles-table-clamp-2" title={a.comment}>
                    {a.comment}
                  </div>
                )}
              />

              <Column
                // the issues button is 85px wide (ColorBox), plus the cell padding
                width={110}
                flexGrow={0}
                flexShrink={0}
                disableSort={true}
                label="Issues"
                dataKey="issues"
                cellRenderer={({ rowData: a }: { rowData: OrderArticle }) => (
                  <>
                    {a.issues?.length > 0 && (
                      <div id={`colorbox-${a.ptArticleId}`}>
                        <ColorBox
                          type="prop"
                          name="issues"
                          state="issues"
                          active
                          clickable
                          numIssues={a.issues?.length}
                          onClick={() => {
                            if (parentPage === 'ordersTable' && !viewOnly) {
                              setSelectedArticle(a)
                              setOpenReportIssueDialog(true)
                            } else {
                              setSelectedArticle(a)
                              setAnchorIssueBtn(
                                document.getElementById(`colorbox-${a.ptArticleId}`)
                              )
                              setOpenIssueDetailPopper(true)
                            }
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              />
            </Table>
          )}
        </AutoSizer>
      </div>

      {anchorIssueBtn && (
        <GenericContentPopper
          open={openIssueDetailPopper}
          anchorEl={anchorIssueBtn}
          setAnchorEl={setAnchorIssueBtn}
          style={{ zIndex: 2000 }}
        >
          <div className="articles-table-popper-issues">
            {selectedArticle?.issues[0].description}
          </div>
        </GenericContentPopper>
      )}
    </>
  )
}
